import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { LogoHome } from '../components/LogoHome';
import { AssignWorkersDialog } from '../components/AssignWorkersDialog';
import { ArrowLeft, LogOut, Download, Users, User as UserIcon, Search, AlertTriangle, UserPlus, Pin, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

function fmtRelative(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}

function csvEscape(s) {
  if (s == null) return '';
  const v = String(s);
  return v.includes(',') || v.includes('"') || v.includes('\n')
    ? `"${v.replace(/"/g, '""')}"`
    : v;
}

export default function TeamAllocationsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('all');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('by-client');
  const [assignDialogClient, setAssignDialogClient] = useState(null);

  const backRoute = user?.role === 'organisation' ? '/admin' : '/supervisor';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/team-allocations?scope=${scope}`, { headers: authHeader() });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load allocations');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const filteredClients = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.clients_view;
    return data.clients_view.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.workers.some(w => w.name.toLowerCase().includes(q))
    );
  }, [data, search]);

  const filteredWorkers = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.workers_view;
    return data.workers_view.filter(w =>
      w.name.toLowerCase().includes(q) ||
      (w.email && w.email.toLowerCase().includes(q)) ||
      w.clients.some(c => c.name.toLowerCase().includes(q))
    );
  }, [data, search]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [['Client', 'Worker', 'Role', 'Total Shifts', 'Upcoming Shifts', 'Last Shift']];
    for (const c of data.clients_view) {
      if (c.workers.length === 0) {
        rows.push([c.name, '(no workers)', '', 0, 0, '']);
      } else {
        for (const w of c.workers) {
          rows.push([c.name, w.name, w.role, w.total_shifts, w.upcoming_shifts, fmtRelative(w.last_shift_at)]);
        }
      }
    }
    const csv = rows.map(r => r.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' }).replace(/\//g, '-');
    a.download = `visitex_team_allocations_${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const renderEmptyBadge = (label) => (
    <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">
      <AlertTriangle className="h-3 w-3 mr-1" />
      {label}
    </Badge>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="team-allocations-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoHome className="h-10 w-auto" />
            <div className="hidden sm:block">
              <h1 className="font-semibold text-slate-900 text-sm">Team Allocations</h1>
              <p className="text-xs text-slate-500">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to={backRoute} className="text-sm text-slate-500 hover:text-teal-600 flex items-center gap-1">
              <ArrowLeft className="h-4 w-4" /> Dashboard
            </Link>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Summary KPIs */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="alloc-summary">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase">Clients</p>
                <p className="text-2xl font-semibold text-slate-900">{data.summary.total_clients}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase">Workers</p>
                <p className="text-2xl font-semibold text-slate-900">{data.summary.total_workers}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase">Active Pairings</p>
                <p className="text-2xl font-semibold text-teal-700">{data.summary.total_pairs}</p>
              </CardContent>
            </Card>
            <Card className={data.summary.clients_with_no_worker > 0 ? 'border-amber-300' : ''}>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase">Clients With No Worker</p>
                <p className={`text-2xl font-semibold ${data.summary.clients_with_no_worker > 0 ? 'text-amber-600' : 'text-slate-900'}`}>
                  {data.summary.clients_with_no_worker}
                </p>
              </CardContent>
            </Card>
            <Card className={data.summary.workers_with_no_client > 0 ? 'border-amber-300' : ''}>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase">Workers With No Client</p>
                <p className={`text-2xl font-semibold ${data.summary.workers_with_no_client > 0 ? 'text-amber-600' : 'text-slate-900'}`}>
                  {data.summary.workers_with_no_client}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Controls */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <CardTitle className="text-base">Allocation Matrix</CardTitle>
              <div className="flex flex-wrap gap-2 items-center">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search clients or workers..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8 h-9 w-64"
                    data-testid="alloc-search"
                  />
                </div>
                <Select value={scope} onValueChange={setScope}>
                  <SelectTrigger className="h-9 w-40" data-testid="alloc-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All time</SelectItem>
                    <SelectItem value="upcoming">Upcoming only</SelectItem>
                    <SelectItem value="history">History only</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={exportCsv} variant="outline" size="sm" data-testid="alloc-export-btn" disabled={!data}>
                  <Download className="h-4 w-4 mr-1" /> CSV
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={view} onValueChange={setView}>
              <TabsList>
                <TabsTrigger value="by-client" data-testid="tab-by-client">
                  <Users className="h-4 w-4 mr-1" /> By Client ({filteredClients.length})
                </TabsTrigger>
                <TabsTrigger value="by-worker" data-testid="tab-by-worker">
                  <UserIcon className="h-4 w-4 mr-1" /> By Worker ({filteredWorkers.length})
                </TabsTrigger>
              </TabsList>

              {/* BY CLIENT */}
              <TabsContent value="by-client" className="mt-4">
                {loading ? (
                  <div className="py-12 text-center text-slate-400">Loading…</div>
                ) : filteredClients.length === 0 ? (
                  <div className="py-12 text-center text-slate-400">No clients match this filter.</div>
                ) : (
                  <div className="space-y-3">
                    {filteredClients.map((c) => (
                      <div key={c.id} className="border border-slate-200 rounded-lg p-4 bg-white" data-testid={`alloc-client-${c.id}`}>
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <h3 className="font-semibold text-slate-900 truncate">{c.name}</h3>
                            {c.status === 'archived' && <Badge variant="outline" className="text-slate-500">archived</Badge>}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {c.workers.length === 0
                              ? renderEmptyBadge('No workers')
                              : <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100">{c.worker_count} worker{c.worker_count !== 1 ? 's' : ''}</Badge>}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setAssignDialogClient(c)}
                              data-testid={`assign-workers-btn-${c.id}`}
                              className="border-teal-300 text-teal-700 hover:bg-teal-50"
                            >
                              <UserPlus className="h-4 w-4 mr-1" /> Assign
                            </Button>
                          </div>
                        </div>
                        {c.workers.length > 0 && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
                            {c.workers.map((w) => (
                              <div key={w.id} className={`border rounded p-2.5 text-sm ${w.pinned ? 'border-teal-300 bg-teal-50/40' : 'border-slate-100 bg-slate-50'}`} data-testid={`alloc-pair-${c.id}-${w.id}`}>
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-medium text-slate-800 truncate">{w.name}</span>
                                  <div className="flex items-center gap-1 shrink-0">
                                    {w.pinned && (
                                      <Badge variant="outline" className="text-[10px] border-teal-400 text-teal-700 bg-white">
                                        <Pin className="h-2.5 w-2.5 mr-0.5" />Pinned
                                      </Badge>
                                    )}
                                    <Badge variant="outline" className="text-[10px] uppercase">{w.role}</Badge>
                                  </div>
                                </div>
                                <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                                  <span><strong className="text-slate-700">{w.total_shifts}</strong> total</span>
                                  <span><strong className={w.upcoming_shifts > 0 ? 'text-emerald-700' : 'text-slate-700'}>{w.upcoming_shifts}</strong> upcoming</span>
                                  {w.last_shift_at && <span className="text-slate-400">last: {fmtRelative(w.last_shift_at)}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* BY WORKER */}
              <TabsContent value="by-worker" className="mt-4">
                {loading ? (
                  <div className="py-12 text-center text-slate-400">Loading…</div>
                ) : filteredWorkers.length === 0 ? (
                  <div className="py-12 text-center text-slate-400">No workers match this filter.</div>
                ) : (
                  <div className="space-y-3">
                    {filteredWorkers.map((w) => (
                      <div key={w.id} className="border border-slate-200 rounded-lg p-4 bg-white" data-testid={`alloc-worker-${w.id}`}>
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <div className="flex items-center gap-2 flex-wrap min-w-0">
                            <h3 className="font-semibold text-slate-900">{w.name}</h3>
                            <Badge variant="outline" className="text-[10px] uppercase">{w.role}</Badge>
                            {w.email && <span className="text-xs text-slate-400 truncate">{w.email}</span>}
                            <span className="text-xs text-slate-500 inline-flex items-center gap-1" data-testid={`alloc-worker-last-${w.id}`}>
                              <Clock className="h-3 w-3" />
                              {w.last_clock_in_at ? (() => {
                                const diffMs = Date.now() - new Date(w.last_clock_in_at).getTime();
                                const days = Math.floor(diffMs / 86400000);
                                if (days === 0) return 'Last clock-in: today';
                                if (days === 1) return 'Last clock-in: yesterday';
                                if (days < 7) return `Last clock-in: ${days}d ago`;
                                return `Last clock-in: ${new Date(w.last_clock_in_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: 'short', year: 'numeric' })}`;
                              })() : <span className="italic text-amber-600">never clocked in</span>}
                            </span>
                          </div>
                          {w.clients.length === 0
                            ? renderEmptyBadge('No client allocations')
                            : <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100">{w.client_count} client{w.client_count !== 1 ? 's' : ''}</Badge>}
                        </div>
                        {w.clients.length > 0 && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
                            {w.clients.map((c) => (
                              <div key={c.id} className={`border rounded p-2.5 text-sm ${c.pinned ? 'border-teal-300 bg-teal-50/40' : 'border-slate-100 bg-slate-50'}`}>
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-medium text-slate-800 truncate">{c.name}</span>
                                  <div className="flex items-center gap-1 shrink-0">
                                    {c.pinned && (
                                      <Badge variant="outline" className="text-[10px] border-teal-400 text-teal-700 bg-white">
                                        <Pin className="h-2.5 w-2.5 mr-0.5" />Pinned
                                      </Badge>
                                    )}
                                    {c.status === 'archived' && <Badge variant="outline" className="text-[10px] text-slate-500">archived</Badge>}
                                  </div>
                                </div>
                                <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                                  <span><strong className="text-slate-700">{c.total_shifts}</strong> total</span>
                                  <span><strong className={c.upcoming_shifts > 0 ? 'text-emerald-700' : 'text-slate-700'}>{c.upcoming_shifts}</strong> upcoming</span>
                                  {c.last_shift_at && <span className="text-slate-400">last: {fmtRelative(c.last_shift_at)}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </main>

      {/* Assign Workers Dialog */}
      <AssignWorkersDialog
        open={!!assignDialogClient}
        onOpenChange={(o) => !o && setAssignDialogClient(null)}
        client={assignDialogClient ? {
          id: assignDialogClient.id,
          name: assignDialogClient.name,
          // pre-tick currently pinned workers
          assigned_worker_ids: assignDialogClient.workers.filter(w => w.pinned).map(w => w.id),
        } : null}
        onSaved={load}
      />
    </div>
  );
}
